
import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import ConversationContainer from "@/components/messages/ConversationContainer";
import MessageList from "@/components/messages/MessageList";
import { useMessageThreadsQuery } from "@/hooks/messages/useMessageThreadsQuery";
import { useIsMobile } from "@/hooks/use-mobile";

const ConversationPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // For message threads in desktop sidebar
  const { threads, isLoading: isThreadsLoading, refetch } = useMessageThreadsQuery();
  
  // Redirect if not logged in
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Redirect if no userId
  if (!userId) {
    return <Navigate to="/messages" />;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <Navbar />
      
      <main className="flex-grow flex overflow-hidden">
        <div className="flex w-full h-full overflow-hidden">
          <div className="flex flex-col md:flex-row w-full h-full">
            {/* Message threads sidebar - only visible on desktop */}
            {!isMobile && (
              <div className="hidden md:flex md:flex-col md:w-1/3 lg:w-1/4 bg-background shadow border-r overflow-hidden">
                <div className="p-4 border-b">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate("/messages")}
                    className="mb-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Messages
                  </Button>
                  <h2 className="font-semibold">Recent Conversations</h2>
                </div>
                <div className="flex-grow overflow-y-auto">
                  <MessageList 
                    threads={threads} 
                    isLoading={isThreadsLoading}
                    onRefresh={refetch}
                  />
                </div>
              </div>
            )}
            
            {/* Main conversation area - takes full width on mobile, partial on desktop */}
            <div className={`flex flex-col ${isMobile ? 'w-full' : 'md:w-2/3 lg:w-3/4'} bg-background overflow-hidden`}>
              {isMobile && (
                <div className="p-4 border-b">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate("/messages")}
                    className="mr-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Messages
                  </Button>
                </div>
              )}
              <ConversationContainer
                userId={userId}
                onBack={() => navigate("/messages")}
              />
            </div>
          </div>
        </div>
      </main>
      
      {/* Footer has been removed as requested */}
    </div>
  );
};

export default ConversationPage;
